import { Box, Typography } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import { Authorization_V1 } from "@rw1/auto-generated-clients";
import { AuthorizationResources } from "@rw1/permissions";
import { FC, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import {
    ConfirmationDialog,
    SearchBar,
    SelectableList,
    SelectableListItem,
} from "../../../components";
import { useAllRoles } from "../../../hooks/useAllRoles";
import { useCompaniesData } from "../../../hooks/useCompaniesData";
import { useCurrentUserRoles } from '../../../hooks/useCurrentUserRoles';
import { useLocalization } from "../../../hooks/useLocalization";
import { formatRole } from "../UsersPage";

interface GrantRoleDialogProps {
    isOpen: boolean;
    assignedRoles?: Authorization_V1.UserRole[];
    onClose: () => void;
    onGrantRole: (roleId: string, organizationId?: string) => Promise<void>;
}


const getHttpStatus = (error: unknown): number | undefined => {
    if (!error || typeof error !== "object") return undefined;

    const err = error as Record<string, unknown>;

    if (typeof err.status === "number" && err.status !== 0) {
        return err.status;
    }

    if (typeof err.statusCode === "number" && err.statusCode !== 0) {
        return err.statusCode;
    }

    if (err.cause) {
        return getHttpStatus(err.cause);
    }

    return undefined;
};

const getRoleAssignmentKey = (roleId: string, organizationId?: string) =>
    `${roleId}|${organizationId ?? ""}`;

const isRoleOrganizationSpecific = (role: Authorization_V1.Role) =>
    Boolean(
        !role.isAdministrative ||
        role.rolePatterns?.some((p) => p.pattern.includes("{organizationId}"))
    );

export const GrantRoleDialog: FC<GrantRoleDialogProps> = ({
    isOpen,
    assignedRoles,
    onClose,
    onGrantRole,
}) => {
    const { t } = useLocalization();
    const theme = useTheme();
    const { allRoles } = useAllRoles();
    const { companiesData } = useCompaniesData({ enabled: true });
    const { userRoles: currentUserRoles } = useCurrentUserRoles();
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);

    const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleReset = useCallback(() => {
        setSelectedRoleId(null);
        setSelectedCompanyId(null);
        setSearchTerm("");
        setIsSubmitting(false);
        setErrorMessage(null);
    }, []);

    // Automatically reset component state whenever dialog closes or opens
    useEffect(() => {
        if (!isOpen) {
            handleReset();
        }
    }, [isOpen, handleReset]);

    const isAdministrator = useMemo(() => {
        return currentUserRoles?.some(
            (r) => r.roleId === AuthorizationResources.Global.Group.Administrator
        );
    }, [currentUserRoles]);

    // Companies accessible to the current user
    const accessibleCompanies = useMemo(() => {
        return companiesData
            ?.filter((x) =>
                currentUserRoles?.some(
                    (r) =>
                        r.roleId === AuthorizationResources.Global.Group.Administrator ||
                        r.organizationId === x.companyId
                )
            )
            .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));
    }, [companiesData, currentUserRoles]);

    // Filter available roles:
    // - Admins can grant any role.
    // - Non-admins can grant any role as long as it is NOT administrative.
    const grantableRoles = useMemo(() => {
        if (isAdministrator) {
            return allRoles;
        }

        return allRoles.filter((role) => !role.isAdministrative);
    }, [allRoles, isAdministrator]);

    const selectedRole = useMemo<Authorization_V1.Role | undefined>(
        () => grantableRoles.find((r) => r.roleId === selectedRoleId),
        [grantableRoles, selectedRoleId]
    );

    const requiresOrganization = useMemo(() => {
        if (!selectedRole) {
            return false;
        }

        return isRoleOrganizationSpecific(selectedRole);
    }, [selectedRole]);

    const assignedRoleKeys = useMemo(
        () =>
            new Set(
                (assignedRoles ?? []).map((r) =>
                    getRoleAssignmentKey(r.roleId, r.organizationId)
                )
            ),
        [assignedRoles]
    );

    // Show company selection step ONLY if role requires org AND there are multiple companies to pick from
    const isOrganizationSelectionStep = Boolean(
        selectedRoleId && requiresOrganization && (accessibleCompanies?.length ?? 0) > 1
    );

    // Determines if we are currently displaying a confirmation message instead of a list
    const isConfirmationStep = Boolean(
        selectedRoleId && (!requiresOrganization || (accessibleCompanies?.length ?? 0) <= 1)
    );

    const roleItems = useMemo<SelectableListItem[]>(() => {
        return grantableRoles.map((role) => {
            const roleId = role.roleId as string;
            const reqOrg = isRoleOrganizationSpecific(role);
            // Global roles are already assigned if they exist without an organization ID
            const isGlobalAssigned = !reqOrg && assignedRoleKeys.has(getRoleAssignmentKey(roleId, undefined));

            return {
                id: roleId,
                primaryText: formatRole(role.roleName),
                tertiaryText: isGlobalAssigned ? t("users.roles.alreadyAssigned") : undefined,
                type: isGlobalAssigned ? "hasAccess" : "selectable",
            };
        });
    }, [grantableRoles, assignedRoleKeys, t]);

    const companyItems = useMemo<SelectableListItem[]>(() => {
        if (!accessibleCompanies || !selectedRoleId) {
            return [];
        }

        return accessibleCompanies.map((company) => {
            const isAssigned = assignedRoleKeys.has(
                getRoleAssignmentKey(selectedRoleId, company.companyId)
            );

            return {
                id: company.companyId,
                primaryText: company.friendlyName,
                tertiaryText: isAssigned ? t("users.roles.alreadyAssigned") : undefined,
                type: isAssigned ? "hasAccess" : "selectable",
            };
        });
    }, [accessibleCompanies, selectedRoleId, assignedRoleKeys, t]);

    const filteredRoleItems = useMemo(() => {
        if (!deferredSearchTerm) {
            return roleItems;
        }

        return roleItems.filter((item) =>
            item.primaryText.toLowerCase().includes(deferredSearchTerm.toLowerCase())
        );
    }, [roleItems, deferredSearchTerm]);

    const filteredCompanyItems = useMemo(() => {
        if (!deferredSearchTerm) {
            return companyItems;
        }

        return companyItems.filter((item) =>
            item.primaryText.toLowerCase().includes(deferredSearchTerm.toLowerCase())
        );
    }, [companyItems, deferredSearchTerm]);

    const handleRoleSelect = (roleId: string) => {
        setSelectedRoleId(roleId);
        setSearchTerm(""); // Clear search term when advancing from role selection

        const role = grantableRoles.find((r) => r.roleId === roleId);
        const reqOrg = role ? isRoleOrganizationSpecific(role) : false;

        // Auto-assign if there's only 1 company available and it's not already assigned there
        if (reqOrg && accessibleCompanies?.length === 1) {
            const companyId = accessibleCompanies[0]!.companyId;
            if (!assignedRoleKeys.has(getRoleAssignmentKey(roleId, companyId))) {
                setSelectedCompanyId(companyId);
            }
        }
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleAssign = async (): Promise<void> => {
        if (!selectedRoleId) {
            return;
        }
        if (requiresOrganization && !selectedCompanyId && (accessibleCompanies?.length ?? 0) > 1) {
            return;
        }

        const targetOrganizationId = requiresOrganization
            ? selectedCompanyId ?? accessibleCompanies?.[0]?.companyId
            : undefined;

        try {
            setIsSubmitting(true);
            setErrorMessage(null);
            await onGrantRole(selectedRoleId, targetOrganizationId);
            handleClose();
        } catch (error: any) {
            const status = getHttpStatus(error);
            if (status === 400) {
                setErrorMessage(t("users.roles.errors.roleNotAllowedForCompany"));
            } else {
                setErrorMessage(t("users.roles.errors.genericGrantFailed"));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isSingleCompanyAssigned =
        requiresOrganization &&
        (accessibleCompanies?.length ?? 0) === 1 &&
        assignedRoleKeys.has(getRoleAssignmentKey(selectedRoleId ?? "", accessibleCompanies?.[0]?.companyId));

    const isGlobalAssigned =
        !requiresOrganization &&
        assignedRoleKeys.has(getRoleAssignmentKey(selectedRoleId ?? "", undefined));

    const isAssignDisabled =
        !selectedRoleId ||
        isSingleCompanyAssigned ||
        isGlobalAssigned ||
        (requiresOrganization && !selectedCompanyId && (accessibleCompanies?.length ?? 0) > 1) ||
        isSubmitting;

    const secondaryActionButtonText = isOrganizationSelectionStep
        ? t("users.roles.back")
        : t("users.roles.cancel");

    const handleSecondaryActionClick = () => {
        setSearchTerm(""); // Clear search term when going back or canceling
        if (selectedCompanyId && isOrganizationSelectionStep) {
            setSelectedCompanyId(null);
        } else if (selectedRoleId) {
            setSelectedRoleId(null);
            setSelectedCompanyId(null);
        } else {
            handleClose();
        }
    };

    const renderDialogContent = () => {
        if (!selectedRoleId) {
            return (
                <SelectableList
                    emptyMessage={t("users.roles.noRolesFound")}
                    items={filteredRoleItems}
                    maxHeight="100%"
                    minHeight={0}
                    multiSelect={false}
                    onItemSelect={handleRoleSelect}
                    selectedId={selectedRoleId}
                    selectedItems={[]}
                    sx={{ flex: 1, minHeight: 0, overflow: "auto" }} />
            );
        }

        if (isOrganizationSelectionStep) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                        gap: 1,
                    }}>
                    <Typography sx={{ fontWeight: 600, flexShrink: 0 }} variant="subtitle2">
                        {t("users.roles.selectOrganizationForRole")}: {formatRole(selectedRole!.roleName)}
                    </Typography>
                    <SelectableList
                        emptyMessage={t("users.roles.noCompaniesAvailable")}
                        items={filteredCompanyItems}
                        maxHeight="100%"
                        minHeight={0}
                        multiSelect={false}
                        onChipRemove={() => setSelectedCompanyId(null)}
                        onItemSelect={(companyId) => {
                            setSelectedCompanyId(companyId);
                            setSearchTerm(""); // Clear search term after picking company
                        }}
                        selectedId={selectedCompanyId}
                        selectedItems={companyItems.filter(
                            (item) => item.id === selectedCompanyId
                        )}
                        selectedItemsTitle={t("users.roles.selectedCompany")}
                        sx={{ flex: 1, minHeight: 0, overflow: "auto" }} />
                </Box>
            );
        }

        return (
            <Typography sx={{ mt: 2 }} variant="body1">
                {requiresOrganization
                    ? t("users.roles.confirmGrantOrganizationRole", {
                        roleName: formatRole(selectedRole!.roleName),
                        companyName: accessibleCompanies?.[0]?.friendlyName,
                    })
                    : t("users.roles.confirmGrantGlobalRole", {
                        roleName: formatRole(selectedRole!.roleName),
                    })}
            </Typography>
        );
    };

    return (
        <ConfirmationDialog.Root
            fullScreenOnMobile
            fullWidth
            height="700px"
            maxWidth="xs"
            onClose={handleClose}
            open={isOpen}>
            <ConfirmationDialog.Title showIcon={false}>
                <Typography sx={{ fontSize: { xs: 18, md: 24 }, fontWeight: 700 }}>
                    {t("users.roles.grantRole")}
                </Typography>
            </ConfirmationDialog.Title>

            <ConfirmationDialog.Content
                sx={{
                    color: theme.colors.text[1],
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}>

                {errorMessage && (
                    <Typography
                        color="error"
                        sx={{ mb: 2, p: 1.5, backgroundColor: theme.colors.red ? `${theme.palette.error.light}22` : 'rgba(211, 47, 47, 0.1)', borderRadius: 1 }}
                        variant="body2">
                        {errorMessage}
                    </Typography>
                )}

                {!isConfirmationStep && (
                    <SearchBar
                        handleClear={() => setSearchTerm("")}
                        handleSubmit={(e) => e.preventDefault()}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={
                            isOrganizationSelectionStep
                                ? t("users.roles.searchCompanies")
                                : t("users.roles.searchRoles")
                        }
                        sx={{ maxWidth: undefined, mb: 2 }}
                        value={searchTerm} />
                )}

                {renderDialogContent()}
            </ConfirmationDialog.Content>

            <ConfirmationDialog.Actions>
                <ConfirmationDialog.Button
                    onClick={handleSecondaryActionClick}
                    sx={{ color: theme.colors.primary[5] }}
                    variant="secondary">
                    {secondaryActionButtonText}
                </ConfirmationDialog.Button>
                <ConfirmationDialog.Button
                    disabled={isAssignDisabled}
                    isLoading={isSubmitting}
                    onClick={() => {
                        void handleAssign();
                    }}
                    variant="contained">
                    {t("users.roles.grantRole")}
                </ConfirmationDialog.Button>
            </ConfirmationDialog.Actions>
        </ConfirmationDialog.Root>
    );
};
